import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Typography, ListItem, List, Button, makeStyles } from '@ellucian/react-design-system/core';
import { FormattedMessage } from 'react-intl';
import { spacing40, fontWeightBold } from '@ellucian/react-design-system/core/styles/tokens';
import Icon from '@ellucian/ds-icons/lib/es6/Icon';
import { useHistory } from 'react-router-dom';
import useApplicationForm from './useApplicationForm';
import { routes, CONSTANTS } from '../Helpers/Configuration';
import ConditionalLogicCheck from './ConditionalLogicCheck';
import useRoot from '../RootContext/useRoot';
import ApplicationFormSideBarListItem from './ApplicationFormSideBarListItem';
import { useSelector } from 'react-redux';
import { isApplicationSubmitted } from '../Helpers/ApplicationHelpers';
const useStyles = makeStyles(theme => ({
    action: {
        margin: spacing40,
        '& svg': {
            marginRight: spacing40,
        },
    },
    listItem: {
        cursor: 'pointer',
    },
    pageList: {
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        maxWidth: '300px',
    },
    title: {
        fontWeight: fontWeightBold,
        margin: spacing40,
    },
    [theme.breakpoints.down('sm')]: {
        pageList: {
            maxWidth: '100%',
        },
    },
}));
const selectApplicationIsSubmitted = state => isApplicationSubmitted(state.application);
const selectApplicationIsArchived = state => state.application?.archivedOn;
const selectApplicationIsWithdrawn = state => state.application?.status === CONSTANTS.applicationFormStatuses.WITHDRAWN;
const selectApplicationIsPaymentPending = state => state.application?.paymentInformation?.paymentStatus === CONSTANTS.applicationPaymentInformation.paymentStatus.pending;

const ApplicationFormSideBar = props => {
    const {
        onPageClick: onPageClickProp,
        page = null,
    } = props;
    const {
        clearApplicationSession,
        externalMode,
        importAdminMode,
        previewMode,
        template = { pages: [] },
    } = useApplicationForm();
    const rootCtx = useRoot();
    const classes = useStyles();
    const history = useHistory();
    const isSubmitted = useSelector(selectApplicationIsSubmitted);
    const isArchived = useSelector(selectApplicationIsArchived);
    const isWithdrawn = useSelector(selectApplicationIsWithdrawn);
    const isPaymentPending = useSelector(selectApplicationIsPaymentPending);
    const goToApplicationsHome = useCallback(() => {
        clearApplicationSession();
        rootCtx.clearTheme();
        history.push(routes.home);
    }, [clearApplicationSession, history]);
    const onPageClick = useCallback(value => {
        onPageClickProp && onPageClickProp(value);
    }, [onPageClickProp]);
    const goToSupplementalChecklist = () => {
        onPageClick(CONSTANTS.pageNames.supplementalItems);
    };
    const goToApplicationSubmitted = () => {
        onPageClick(CONSTANTS.pageNames.submission);
    };
    const goToReview = () => {
        onPageClick(CONSTANTS.pageNames.review);
    };
    return (
        <div>
            <div className={classes.action}>
                {!externalMode && !importAdminMode &&
                    <Button disabled={previewMode} variant="text" onClick={goToApplicationsHome}>
                        <Icon name="home" />
                        <FormattedMessage id="applicationsHome" />
                    </Button>
                }
            </div>
            {(!isSubmitted && !isArchived && !isWithdrawn) &&
                <Typography className={classes.title} component="div">
                    <FormattedMessage id="admissionsRequirements" />
                </Typography>
            }
            <List classes={{ root: classes.pageList }} role="menu">
                {((!isSubmitted && !isPaymentPending) && !isArchived && !isWithdrawn) && template.pages.map(it => {
                    return (
                        <ConditionalLogicCheck key={it.id}
                            conditions={it.conditions}
                            content={it}
                            contentType={CONSTANTS.appTemplateControlTypes.page}
                        >
                            <ApplicationFormSideBarListItem
                                active={it.id === page?.id}
                                onClick={onPageClick}
                                page={it}
                            />
                        </ConditionalLogicCheck>
                    );
                })}
                {(!isSubmitted && !isArchived && !isWithdrawn && !importAdminMode) &&
                    <ListItem divider onClick={goToReview} className={classes.listItem} role="menuitem">
                        <Typography>
                            <FormattedMessage id="reviewAndSubmit" />
                        </Typography>
                    </ListItem>
                }
                {(isSubmitted || isArchived || isWithdrawn) &&
                    <>
                        <ListItem divider onClick={goToApplicationSubmitted} className={classes.listItem} role="menuitem">
                            <Typography>
                                <FormattedMessage id="applicationView" />
                            </Typography>
                        </ListItem>
                        {template?.settings?.enableSupplementalItems && !!template?.supplementalItems?.items?.length && template?.supplementalItems?.items?.length > 0 &&
                            <ListItem divider onClick={goToSupplementalChecklist} className={classes.listItem} role="menuitem">
                                <Typography>
                                    <FormattedMessage id="supplementalItemsChecklist" />
                                </Typography>
                            </ListItem>
                        }
                    </>
                }
            </List>
        </div>
    );
};
ApplicationFormSideBar.propTypes = {
    onPageClick: PropTypes.func,
    page: PropTypes.object,
};
export default ApplicationFormSideBar;
